import { AccountRole, AdminPermission } from '@ivy-monorepo/shared'
import NavItem from './NavItem'
import { usePermissions } from '../contexts/PermissionsContext'

interface NavItemListProps {
  role: string
  isCollapsed: boolean
  isMobile?: boolean
}

interface AdminNavItem {
  label: string
  icon: string
  href: string
  permission: AdminPermission | AdminPermission[]
  disabled?: boolean
}

const USERS_NAV_PERMISSIONS = [
  AdminPermission.Users,
  AdminPermission.CheckedInUsers,
  AdminPermission.Admin,
  AdminPermission.AuthenticatedAccounts,
]

const USER_INTERACTIONS_NAV_PERMISSIONS = [
  AdminPermission.Likes,
  AdminPermission.Blocks,
  AdminPermission.Conversations,
]

const VENUE_ACTIVITY_NAV_PERMISSIONS = [
  AdminPermission.Visits,
  AdminPermission.Breadcrumbs,
]

const ADMIN_NAV_ITEMS: AdminNavItem[] = [
  { label: 'Dashboard', icon: 'LayerGroup', href: '/admin/dashboard', permission: AdminPermission.Dashboard },
  { label: 'Users', icon: 'Users', href: '/admin/users', permission: USERS_NAV_PERMISSIONS },
  { label: 'Venues', icon: 'Shop', href: '/admin/venues', permission: AdminPermission.Venues },
  { label: 'User activity', icon: 'UserGroup', href: '/admin/user-interactions', permission: USER_INTERACTIONS_NAV_PERMISSIONS, },
  { label: 'Venue activity', icon: 'DoorOpen', href: '/admin/venue-activity', permission: VENUE_ACTIVITY_NAV_PERMISSIONS, },
  { label: 'Stickers', icon: 'Images', href: '/admin/stickers', permission: AdminPermission.Stickers },
  { label: 'RSVPs', icon: 'CalendarDays', href: '/admin/venue-events', permission: AdminPermission.Events },
  { label: 'Eventbrite', icon: 'CalendarPlus', href: '/admin/eventbrite', permission: AdminPermission.Venues },
  { label: 'Tickets', icon: 'Ticket', href: '/admin/tickets', permission: AdminPermission.Tickets },
  { label: 'Partnered hotspots', icon: 'Handshake', href: '/admin/partnered-hotspots', permission: AdminPermission.Events },
  { label: 'Customers', icon: 'MoneyBill', href: '/admin/customers', permission: AdminPermission.Customers, disabled: true },
  { label: 'Roles', icon: 'Key', href: '/admin/roles', permission: AdminPermission.Admin },
  { label: 'App launches', icon: 'MobileScreen', href: '/admin/app-launches', permission: AdminPermission.AppLaunches },
  { label: 'Audit logs', icon: 'ClipboardList', href: '/admin/audit-logs', permission: AdminPermission.AuditLogs },
  { label: 'Feature flags', icon: 'ToggleOn', href: '/admin/feature-flags', permission: AdminPermission.FeatureFlags },
  { label: 'Dev telephones', icon: 'MobileScreenButton', href: '/admin/dev-telephones', permission: AdminPermission.DevTelephoneNumber },
  { label: 'Simulation', icon: 'MasksTheater', href: '/admin/simulation', permission: AdminPermission.Simulation },
  { label: 'Intentions', icon: 'Bullseye', href: '/admin/intentions', permission: AdminPermission.Intentions },
  { label: 'Reports', icon: 'FileLines', href: '/admin/reports', permission: AdminPermission.Reports },
  { label: 'Scheduled jobs', icon: 'PersonDigging', href: '/admin/jobs', permission: AdminPermission.Jobs },
  { label: 'System status', icon: 'HeartPulse', href: '/admin/system-status', permission: AdminPermission.SystemStatus },
  { label: 'Photo verification', icon: 'IdCard', href: '/admin/photo-verification', permission: AdminPermission.PhotoVerification },
  { label: 'Notifications', icon: 'Bell', href: '/admin/notifications', permission: AdminPermission.Notifications },
  { label: 'Feedback', icon: 'Bullhorn', href: '/admin/comments', permission: AdminPermission.Comments },
  { label: 'Map', icon: 'Map', href: '/admin/map', permission: AdminPermission.Map },
  { label: 'Live map', icon: 'LocationDot', href: '/admin/live-map', permission: AdminPermission.Map },
]

const AdminNavItemList = ({ isCollapsed, isMobile }: { isCollapsed: boolean; isMobile?: boolean }) => {
  const { hasPermission, isLoading } = usePermissions()
  const navClasses = `nav flex-column ${isCollapsed ? 'collapsed' : ''} ${isMobile ? 'mobile' : ''}`

  const visibleItems = isLoading
    ? ADMIN_NAV_ITEMS
    : ADMIN_NAV_ITEMS.filter(item => {
        const required = Array.isArray(item.permission) ? item.permission : [item.permission]
        return required.some(permission => hasPermission(permission))
      })

  return (
    <ul className={navClasses}>
      {visibleItems.map(item => (
        <NavItem key={item.href} label={item.label} icon={item.icon} href={item.href} disabled={item.disabled} />
      ))}
    </ul>
  )
}

const NavItemList = ({ role, isCollapsed, isMobile }: NavItemListProps) => {
  const navClasses = `nav flex-column ${isCollapsed ? 'collapsed' : ''} ${isMobile ? 'mobile' : ''}`

  switch (role) {
    case AccountRole.Admin:
      return <AdminNavItemList isCollapsed={isCollapsed} isMobile={isMobile} />

    case AccountRole.Customer:
      return (
        <ul className={navClasses}>
          <NavItem label={`Dashboard`} icon="Home" href="/customer/dashboard" />
          <NavItem label={`Venues`} icon="Shop" href="/customer/venues" />
        </ul>
      )

    default:
      return <></>
  }
}

export default NavItemList
